import axios from 'axios'
import { i18n } from '../../../plugins/i18n'
import { camelCase } from '../../../modules/string_converter'
import { getQuery } from '../../../modules/getQuery'
import {
  visitorsTypes,
  crossAnalysisTypes,
  periods,
  displayAllPrefix,
  generateGraphId
} from '../../../modules/analysis'

const dataType = (visitorsTypes.find(v => v.id === getQuery('type')) || visitorsTypes[0]).dataType
const pageType = crossAnalysisTypes.find(v => v.fields === getQuery('fields')) || crossAnalysisTypes[0]

export const companiesAnalysisVisitors = {
  namespaced: true,
  state: {
    rawAggs: [],                      // API で取得したテーブルデータ
    plots: {},                        // API で取得したグラフデータ
    aggsLoading: false,               // テーブルデータ取得中かどうか
    plotsLoading: false,              // グラフデータ取得中かどうか
    dataType,                         // データ種別
    date: periods[1],                 // 日, 週, 月
    displayGraphs: [],                // グラフ表示対象
    graphKey: [],
    individualAnalysisField: null,    // テーブル表示リスト（= ドリルダウン対象のテーブルの行のfield）
    pageType,                         // 分析軸
    crossSearchField: null,           // クロス分析
  },
  getters: {
    /**
     * API に渡すパラメータ
     * @param { Object } state
     * @param { Object } getters
     * @returns { Object }
     */
    params: (state, getters) => {
      return {
        data_type: state.dataType,
        period: state.date.id,                                          // ex. 'week'
        fields: getters.crossSearchFields.map(v => v.fields).join(','), // store_id
        sort_key: '',
        sort_order: '',
      }
    },
    /**
     * クロス分析の項目一覧
     * @param { Object } state
     * @returns { Array }
     */
    crossSearchFields(state) {
      const arr = [state.pageType]
      if (state.crossSearchField) {
        arr.push(state.crossSearchField)
      }
      return arr
    },
    /**
     * データ種別の項目一覧
     * @param { Object } state
     * @param { Object } getters
     * @param { Object } rootState
     * @returns { Array }
     */
    dataTypes: (state, getters, rootState) => {
      const availableVisitorsTypesIds = ['lpPvs', 'likeCount', 'dislikeCount', 'surveyAnswerCount']
      if (rootState.companiesAvailableSites.data) {
        availableVisitorsTypesIds.push(
          ...rootState.companiesAvailableSites.data.map(site => {
            return `${camelCase(site.attributes.site_type)}ImpressionCount`
          })
        )
      }
      return visitorsTypes.filter(vt => availableVisitorsTypesIds.includes(vt.id))
    },
    /**
     * 分析軸の項目一覧
     * @returns { Array }
     */
    pageTypes(state, getters, rootState, rootGetters) {
      const ignoreAxes = ['site']
      if (!rootGetters['companiesCompany/inboundOption']) ignoreAxes.push('locale')
      return rootState.companiesAnalysisAxes.data.filter(v => !ignoreAxes.includes(v.id))
    },
    /**
     * テーブル レコードID
     * @param { Object } state
     * @returns { String }
     */
    individualAnalysisId(state) {
      return state.individualAnalysisField ? state.individualAnalysisField.id : null
    },
    /**
     * テーブル レコード名
     * @param { Object } state
     * @returns { String }
     */
    individualAnalysisName(state) {
      return state.individualAnalysisField ? state.individualAnalysisField.name : ''
    },
    /**
     * 選択中のレコード
     * @param { Object } state
     * @returns { Array }
     */
    selectedAggIds: state => {
      return state.displayGraphs
    },
    /**
     * テーブルデータ
     * @param { Object } state
     * @param { Object } getters
     * @returns { String }
     */
    aggs(state, getters) {
      if (state.rawAggs.length === 0) return state.rawAggs

      let aggs = JSON.parse(JSON.stringify(state.rawAggs))
      if(getters.individualAnalysisId) {
        // 個別分析idでaggsを絞り込み
        aggs = aggs.filter(agg => {
          const currentPageTypeField = agg.fields.find(field => field.key === state.pageType.fields)
          return currentPageTypeField.id === getters.individualAnalysisId
        })
      }

      // テーブルの「全」系のfieldsを作成
      const fields = aggs[0].fields.map(field => {
        return {
          id: 0,
          key: field.key,
          name: i18n.t('models.analysis.crossAnalysisTypes.allName')
        }
      })

      if(getters.individualAnalysisId) {
        // テーブル「表示リスト全て」の列の値を変更
        fields[0].name = i18n.t('models.analysis.displayAll')
        fields[0].id = getters.individualAnalysisId
        fields[0].key = displayAllPrefix + fields[0].key
      }

      // aggの平均や合計を計算して入れる
      const lpPvs = aggs.reduce((acc, current) => acc + Number(current.lpPvs), 0)
      const likeCount = aggs.reduce((acc, current) => acc + Number(current.likeCount), 0)
      const dislikeCount = aggs.reduce((acc, current) => acc + Number(current.dislikeCount), 0)
      const surveyAnswerCount = aggs.reduce((acc, current) => acc + Number(current.surveyAnswerCount), 0)
      const googleImpressionCount = aggs.reduce((acc, current) => acc + Number(current.googleImpressionCount), 0)
      const tripAdvisorImpressionCount = aggs.reduce((acc, current) => acc + Number(current.tripAdvisorImpressionCount), 0)
      const tabelogImpressionCount = aggs.reduce((acc, current) => acc + Number(current.tabelogImpressionCount), 0)
      const rettyImpressionCount = aggs.reduce((acc, current) => acc + Number(current.rettyImpressionCount), 0)
      const hotPepperGourmetImpressionCount = aggs.reduce((acc, current) => acc + Number(current.hotPepperGourmetImpressionCount), 0)
      const gnaviImpressionCount = aggs.reduce((acc, current) => acc + Number(current.gnaviImpressionCount), 0)
      const hotPepperBeautyImpressionCount = aggs.reduce((acc, current) => acc + Number(current.hotPepperBeautyImpressionCount), 0)
      const yahooLocoImpressionCount = aggs.reduce((acc, current) => acc + Number(current.yahooLocoImpressionCount), 0)
      const asoviewImpressionCount = aggs.reduce((acc, current) => acc + Number(current.asoviewImpressionCount), 0)
      const demaecanImpressionCount = aggs.reduce((acc, current) => acc + Number(current.demaecanImpressionCount), 0)
      const ikyuImpressionCount = aggs.reduce((acc, current) => acc + Number(current.ikyuImpressionCount), 0)
      const ozmallImpressionCount = aggs.reduce((acc, current) => acc + Number(current.ozmallImpressionCount), 0)
      const taishuTenpyoImpressionCount = aggs.reduce((acc, current) => acc + Number(current.taishuTenpyoImpressionCount), 0)
      const agg = {
        lpPvs: lpPvs,
        likeCount: likeCount,
        dislikeCount: dislikeCount,
        surveyAnswerCount: surveyAnswerCount,
        googleImpressionCount: googleImpressionCount,
        tripAdvisorImpressionCount: tripAdvisorImpressionCount,
        tabelogImpressionCount: tabelogImpressionCount,
        rettyImpressionCount: rettyImpressionCount,
        hotPepperGourmetImpressionCount: hotPepperGourmetImpressionCount,
        gnaviImpressionCount: gnaviImpressionCount,
        hotPepperBeautyImpressionCount: hotPepperBeautyImpressionCount,
        yahooLocoImpressionCount: yahooLocoImpressionCount,
        asoviewImpressionCount: asoviewImpressionCount,
        demaecanImpressionCount: demaecanImpressionCount,
        ikyuImpressionCount: ikyuImpressionCount,
        ozmallImpressionCount: ozmallImpressionCount,
        taishuTenpyoImpressionCount,
        fields: fields
      }
      aggs.unshift(agg)
      return aggs
    },
    /**
     * @param { Object } state
     * @param { Object } getters
     * @returns { String }
     */
    exportParams: (state, getters) => {
      return {
        // 全ての分析軸
        data_types: getters.dataTypes.map(vt => vt.dataType).join(','),
        // ex. 'week'
        period: state.date.id,
        // 現在の分析軸,クロス分析の条件追加 ex. 'store_id,user_company_id'
        fields: getters.crossSearchFields.map(v => v.fields).join(','),
        field_key_values_list: getters.allAggIds.join(','),
      }
    },
    /**
     * @param { Object } state
     * @returns { String }
     */
    allAggIds: state => {
      return state.rawAggs.map(generateGraphId)
    }
  },
  mutations: {
    /**
     * データ種別の変更
     * @param { Object } state
     * @param { String } dataType
     * @return { Void }
     */
    setDataType(state, dataType) {
      state.plots = {}
      state.dataType = dataType
    },
    /**
     * 日, 週, 月の変更
     * @param { Object } state
     * @param { String } date
     * @return { Void }
     */
    setDate(state, date) {
      state.plots = {}
      state.date = date
    },
    /**
     * グラフ表示対象の変更
     * @param { Object } state
     * @param { String } graphs
     * @return { Void }
     */
    setDisplayGraphs(state, graphs) {
      state.displayGraphs = graphs
    },
    /**
     * 分析軸の変更
     * @param { Object } state
     * @param { Object } axisObj
     * @return { Void }
     */
    setPageType(state, axisObj) {
      state.pageType = axisObj
    },
    /**
     * クロス分析の変更
     * @param { Object } state
     * @param { String } field
     * @return { Void }
     */
    setCrossSearchField(state, field) {
      state.crossSearchField = field
    },
    /**
     * テーブル表示リストの変更
     * @param { Object } state
     * @param { String } field
     * @return { Void }
     */
    setIndividualAnalysisField(state, field) {
      state.individualAnalysisField = field
    },
    /**
     * テーブルデータの取得
     * @param { Object } state
     * @return { Void }
     */
    readAggs(state) {
      state.aggsLoading = true
      state.plots = {}
      state.rawAggs = []
    },
    /**
     * テーブルデータの取得成功
     * @param { Object } state
     * @return { Void }
     */
    readAggsSuccess(state, data) {
      state.rawAggs = data.aggs.map(v => ({
        // ここの key は visitorsTypes の id と合わせること
        lpPvs: v.lp_pv,
        likeCount: v.like_count,
        dislikeCount: v.dislike_count,
        surveyAnswerCount: v.survey_answer_count,
        googleImpressionCount: v.google_impression_count,
        tripAdvisorImpressionCount: v.trip_advisor_impression_count,
        tabelogImpressionCount: v.tabelog_impression_count,
        rettyImpressionCount: v.retty_impression_count,
        hotPepperGourmetImpressionCount: v.hot_pepper_gourmet_impression_count,
        gnaviImpressionCount: v.gnavi_impression_count,
        hotPepperBeautyImpressionCount: v.hot_pepper_beauty_impression_count,
        yahooLocoImpressionCount: v.yahoo_map_impression_count,
        asoviewImpressionCount: v.asoview_impression_count,
        demaecanImpressionCount: v.demaecan_impression_count,
        ikyuImpressionCount: v.ikyu_impression_count,
        ozmallImpressionCount: v.ozmall_impression_count,
        taishuTenpyoImpressionCount: v.taishu_tenpyo_impression_count,
        fields: v.fields,
        storeNames: v.store_names,
      }))
      state.aggsLoading = false
    },
    /**
     * テーブルデータの取得失敗
     * @param { Object } state
     * @return { Void }
     */
    readAggsFailure(state) {
      state.aggsLoading = false
    },
    /**
     * グラフデータの取得
     * @param { Object } state
     * @return { Void }
     */
    readPlots(state) {
      state.plotsLoading = true
    },
    /**
     * グラフデータの取得成功
     * @param { Object } state
     * @param { Array } payload
     * @return { Void }
     */
    readPlotsSuccess(state, payload) {
      // ディープコピー
      const plots = JSON.parse(JSON.stringify(state.plots))
      payload.forEach(res => {
        const data = res.data.data
        const params = res.config.params
        data.forEach((current, index) => {
          if (index === 0) state.graphKey = Object.keys(current.attributes.values)
          if (params.isRequestingDisplayAllPlot) {
            current.attributes.fields[0].key = displayAllPrefix + current.attributes.fields[0].key
            current.attributes.fields[0].name = i18n.t('models.analysis.displayAll')
          }
          const id = generateGraphId(current.attributes)
          if (!Object.prototype.hasOwnProperty.call(plots, id)) {
            plots[id] = { ...current }
          }
        })
      })
      state.plots = plots // objectは代入し直さないとリアクティブにならない
      state.plotsLoading = false
    },
    /**
     * グラフデータの取得失敗
     * @param { Object } state
     * @return { Void }
     */
    readPlotsFailure(state) {
      state.plotsLoading = false
    },
    /**
     * CSV データの取得
     * @param { Object } state
     * @return { Void }
     */
    exportCsv(state) {
      state.loadError = false
      state.errors = null
    },
    /**
     * CSV データの取得 成功
     * @param { Object } state
     * @return { Void }
     */
    exportCsvSuccess(state) {
      state.loadError = false
      state.errors = null
    },
    /**
     * CSV データの取得 失敗
     * @param { Object } state
     * @return { Void }
     */
    exportCsvFailure(state, errors) {
      state.loadError = true
      state.errors = errors
    }
  },
  actions: {
    /**
     * データ種別の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setDataType({ commit }, payload) {
      commit('setDataType', payload)
    },
    /**
     * 日, 週, 月の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setDate({ commit }, payload) {
      commit('setDate', payload)
    },
    /**
     * グラフ表示対象の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setDisplayGraphs({ commit }, payload) {
      commit('setDisplayGraphs', payload)
    },
    /**
     * 分析軸の変更
     * @param { Function } commit
     * @param getters
     * @param { String } queryElement
     * @return { Void }
     */
    setPageType({ commit, state, getters }, queryElement) {
      commit('setPageType', getters.pageTypes.find(v => v.fields === queryElement))
      if (queryElement === state.crossSearchField?.fields) {
        commit('setCrossSearchField', null)
      }
    },
    /**
     * クロス分析の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setCrossSearchField({ commit }, payload) {
      commit('setCrossSearchField', payload)
    },
    /**
     * テーブル表示リストの変更
     * @param { Function } commit
     * @param { Object } getters
     * @param { String } payload
     * @return { Void }
     */
    setIndividualAnalysisField({ commit }, payload) {
      commit('setIndividualAnalysisField', payload)
    },
    /**
     * テーブルデータ取得
     * @param { Function } commit
     * @param { Object } getters
     * @param { String } payload
     * @return { Void }
     */
    readAggs({ commit, getters, state }, payload) {
      const { companyUuid, params } = payload
      commit('readAggs')
      return axios
        .get('/api/companies/' + companyUuid + '/analysis/visitors/aggs.json', {
          headers: {
            'Content-Type': 'application/json'
          },
          params
        })
        .then(res => {
          commit('readAggsSuccess', res.data)
          if (state.displayGraphs.length === 0) {
            commit('setDisplayGraphs', [generateGraphId(getters.aggs[0])])
            return
          }
          let nextDisplayGraphs = getters.aggs
            .filter(agg => getters.selectedAggIds.includes(generateGraphId(agg)))
            // 再リクエストするグラフの順番を維持して、色が変わらないようにする
            .sort((a,b) => getters.selectedAggIds.findIndex(v => v === generateGraphId(a)) - getters.selectedAggIds.findIndex(v => v === generateGraphId(b)))
            .map(agg => generateGraphId(agg))
          if (nextDisplayGraphs.length === 0) {
            nextDisplayGraphs = [generateGraphId(getters.aggs[0])]
          }
          commit('setDisplayGraphs', nextDisplayGraphs)
        })
        .catch(err => {
          commit('readAggsFailure', err)
          console.error('error', err)
        })
    },
    /**
     * グラフデータ取得
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    readPlots({ commit, state, getters }, payload) {
      const { companyUuid, params } = payload
      commit('readPlots')
      // まだ取得していないもののみ取得しに行く
      let graphs = getters.selectedAggIds
        .filter(id => !Object.prototype.hasOwnProperty.call(state.plots, id))
        .map(v => ({ id: v }))
      if (state.individualAnalysisField) {
        graphs = graphs.map(v => {
          if (v.id.includes(displayAllPrefix)) {
            return {
              isRequestingDisplayAllPlot: true,
              id: v.id.replace(displayAllPrefix, '')
            }
          } else {
            return {
              isRequestingDisplayAllPlot: false,
              id: v.id
            }
          }
        })
      }
      const requests = graphs.map(graph => {
        return axios
          .get('/api/companies/' + companyUuid + '/analysis/visitors/plots/'+ graph.id + '.json', {
            headers: {
              'Content-Type': 'application/json'
            },
            params: {
              ...params,
              isRequestingDisplayAllPlot: graph.isRequestingDisplayAllPlot
            }
          })
      })
      return Promise.all(requests)
        .then(responses => {
          commit('readPlotsSuccess', responses)
        })
        .catch(err => {
          commit('readPlotsFailure', err)
          console.error('error', err)
        })
    },
    /**
     * CSV データ取得
     * @param { Function } commit
     * @param { Object } getters
     * @param { String } payload
     * @return { Void }
     */
    exportCsv({ commit, getters }, payload) {
      if (getters.aggs.length == 0) return false

      const { companyUuid, params } = payload
      commit('exportCsv')
      return axios
        .post('/api/companies/' + companyUuid + '/export/analysis/visitors' + '.json',
          params
        )
        .then(() => {
          commit('exportCsvSuccess')
          return true
        })
        .catch(err => {
          commit('exportCsvFailure', err)
          console.error('error', err)
          return false
        })
    }
  }
}
